import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiMapPin, FiPhone, FiMail, FiChevronDown } from 'react-icons/fi';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import TermsOfServiceModal from './TermsOfServiceModal';
import CookiePreferencesLink from './CookiePreferencesLink';
import logo from '../lasmari-wordmark-red.png';

const services = [
  { label: 'Airport Transfers', to: '/airport-transfers' },
  { label: 'North Corfu Transfers', to: '/north-corfu-transfers' },
  { label: 'Port Transfers', to: '/port-transfers' },
  { label: 'Private Tours', to: '/private-tours' },
  { label: 'Hourly Chauffeur', to: '/hourly-chauffeur' },
  { label: 'Wedding Transfers', to: '/wedding-transfers' },
  { label: 'Group Transfers', to: '/group-transfers' },
];

const routes = [
  { label: 'Airport to Kassiopi', to: '/transfers/kassiopi' },
  { label: 'Airport to Sidari', to: '/transfers/sidari' },
  { label: 'Airport to Paleokastritsa', to: '/transfers/paleokastritsa' },
  { label: 'Airport to Corfu Town', to: '/transfers/corfu-town' },
  { label: 'Airport to Gouvia', to: '/transfers/gouvia' },
  { label: 'Airport to Barbati', to: '/transfers/barbati' },
  { label: 'Airport to Acharavi', to: '/transfers/acharavi' },
  { label: 'Airport to Dassia', to: '/transfers/dassia' },
  { label: 'Airport to Kavos', to: '/transfers/kavos' },
  { label: 'Airport to Nissaki', to: '/transfers/nissaki' },
  { label: 'Airport to Ipsos', to: '/transfers/ipsos' },
  { label: 'Airport to Roda', to: '/transfers/roda' },
  { label: 'Airport to Ermones', to: '/transfers/ermones' },
  { label: 'Airport to Glyfada', to: '/transfers/glyfada' },
  { label: 'Airport to Arillas', to: '/transfers/arillas' },
  { label: 'Airport to Agios Stefanos', to: '/transfers/agios-stefanos' },
  { label: 'Airport to Agios Gordios', to: '/transfers/agios-gordios' },
  { label: 'Airport to Benitses', to: '/transfers/benitses' },
  { label: 'Airport to Moraitika', to: '/transfers/moraitika' },
  { label: 'Airport to Lefkimmi', to: '/transfers/lefkimmi' },
];

const guides = [
  { label: 'Corfu Highlights Tour', to: '/tours/corfu-highlights' },
  { label: 'Achilleion Palace Tour', to: '/tours/achilleion-palace' },
  { label: 'Paleokastritsa Tour', to: '/tours/paleokastritsa-tour' },
  { label: 'Cruise Shore Excursion', to: '/tours/shore-excursion' },
  { label: 'Airport to Kassiopi Guide', to: '/blog/corfu-airport-to-kassiopi-complete-guide' },
  { label: 'Airport to Sidari Guide', to: '/blog/corfu-airport-to-sidari-transfer' },
  { label: 'Airport to Paleokastritsa Guide', to: '/blog/corfu-airport-to-paleokastritsa-transfer' },
  { label: 'Airport to Kavos Guide', to: '/blog/corfu-airport-to-kavos-transfer' },
  { label: 'Corfu Transfer Prices 2026', to: '/blog/corfu-transfer-prices-2026' },
  { label: 'How Much Is a Corfu Transfer', to: '/blog/how-much-is-a-corfu-airport-transfer' },
  { label: 'Best Corfu Transfer Service', to: '/blog/best-corfu-transfer-service' },
  { label: 'Private Transfer vs Taxi vs Bus', to: '/blog/corfu-private-transfer-vs-taxi-vs-bus' },
  { label: 'Private Driver vs Rental Car', to: '/blog/corfu-private-driver-vs-rental-car' },
  { label: 'North Corfu Beaches Guide', to: '/blog/north-corfu-beaches-guide' },
  { label: 'Best Time to Visit Corfu', to: '/blog/best-time-to-visit-corfu' },
  { label: 'Corfu Airport Arrival Guide', to: '/blog/corfu-airport-arrival-guide' },
  { label: 'Corfu Airport Pickup Tips', to: '/blog/corfu-airport-pickup-tips' },
  { label: 'Corfu Transportation Guide', to: '/blog/corfu-transportation-guide' },
  { label: 'Is Uber Available in Corfu', to: '/blog/is-uber-available-in-corfu' },
  { label: 'Airport to Hotel Guide', to: '/blog/how-to-get-from-corfu-airport-to-hotel' },
];

const chipClass =
  'text-[11px] text-[#5b4d4f] hover:text-[#ba1609] bg-black/[0.03] hover:bg-[#fbeceb] border border-black/[0.07] hover:border-[#ba1609]/30 rounded-full px-2.5 py-1 transition-all duration-300';

const Footer = () => {
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const currentYear = new Date().getFullYear();

  return (
    <>
      <footer className="relative bg-[#faf7f6] border-t border-black/10 text-[#5b4d4f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-14">
          {/* Top: brand + services + company */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {/* Brand + contact */}
            <div className="lg:col-span-2">
              <img src={logo} alt="Lasmari Chauffeurs" className="h-11 w-auto" />
              <p className="text-sm mt-4 max-w-md leading-relaxed">
                Premium Corfu transfers and private chauffeur service across North Corfu.
                Mercedes-Benz fleet, 24/7 availability. Part of Lasmari Group.
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 text-sm">
                <a href="tel:+302663098633" className="flex items-center gap-2 hover:text-[#ba1609] transition-colors">
                  <FiPhone className="text-[#ba1609]" aria-hidden /> +30 2663098633
                </a>
                <a href="mailto:contact@lasmari.gr" className="flex items-center gap-2 hover:text-[#ba1609] transition-colors">
                  <FiMail className="text-[#ba1609]" aria-hidden /> contact@lasmari.gr
                </a>
                <span className="flex items-center gap-2">
                  <FiMapPin className="text-[#ba1609]" aria-hidden /> Apraos, North Corfu
                </span>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-[#1a1012] text-xs font-semibold mb-4 uppercase tracking-[0.15em]">Services</h4>
              <ul className="space-y-2.5">
                {services.map((s) => (
                  <li key={s.to}>
                    <Link to={s.to} className="text-sm hover:text-[#ba1609] transition-colors">{s.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-[#1a1012] text-xs font-semibold mb-4 uppercase tracking-[0.15em]">Company</h4>
              <ul className="space-y-2.5">
                <li><Link to="/booking" className="text-sm hover:text-[#ba1609] transition-colors">Book Now</Link></li>
                <li><Link to="/faq" className="text-sm hover:text-[#ba1609] transition-colors">FAQ</Link></li>
                <li>
                  <button onClick={() => setShowPrivacyModal(true)} className="text-sm hover:text-[#ba1609] transition-colors">Privacy Policy</button>
                </li>
                <li>
                  <button onClick={() => setShowTermsModal(true)} className="text-sm hover:text-[#ba1609] transition-colors">Terms of Service</button>
                </li>
              </ul>
            </div>
          </div>

          {/* Collapsible SEO link rows (kept in the DOM for SEO, hidden by default to keep the footer compact) */}
          <div className="mt-9 pt-6 border-t border-black/10 space-y-3">
            <details className="footer-disclosure group">
              <summary className="flex items-center justify-between cursor-pointer text-[#1a1012] text-[11px] font-semibold uppercase tracking-[0.15em] hover:text-[#ba1609] transition-colors">
                <span>Popular Corfu Transfer Routes</span>
                <FiChevronDown className="footer-chevron w-4 h-4 text-[#ba1609] transition-transform duration-300" />
              </summary>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {routes.map((r) => (
                  <Link key={r.to} to={r.to} className={chipClass}>{r.label}</Link>
                ))}
              </div>
            </details>

            <details className="footer-disclosure group border-t border-black/10 pt-3">
              <summary className="flex items-center justify-between cursor-pointer text-[#1a1012] text-[11px] font-semibold uppercase tracking-[0.15em] hover:text-[#ba1609] transition-colors">
                <span>Tours &amp; Travel Guides</span>
                <FiChevronDown className="footer-chevron w-4 h-4 text-[#ba1609] transition-transform duration-300" />
              </summary>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {guides.map((g) => (
                  <Link key={g.to} to={g.to} className={chipClass}>{g.label}</Link>
                ))}
              </div>
            </details>
          </div>

          {/* Bottom bar: copyright + agency credit + legal */}
          <div className="mt-9 pt-6 border-t border-black/10 flex flex-col sm:flex-row justify-between items-center gap-3">
            <p className="text-xs text-center sm:text-left">
              © {currentYear} Lasmari Chauffeurs. All rights reserved. · Website by{' '}
              <a
                href="https://corfuwebsites.com"
                target="_blank"
                rel="noopener"
                className="text-[#ba1609] hover:underline underline-offset-4 transition-colors"
              >
                Corfu Websites
              </a>
            </p>
            <div className="flex gap-5 text-xs">
              <button onClick={() => setShowPrivacyModal(true)} className="hover:text-[#ba1609] transition-colors">Privacy Policy</button>
              <button onClick={() => setShowTermsModal(true)} className="hover:text-[#ba1609] transition-colors">Terms of Service</button>
              <CookiePreferencesLink className="hover:text-[#ba1609] transition-colors" />
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <PrivacyPolicyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
      <TermsOfServiceModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
    </>
  );
};

export default Footer;
